import { useStaticQuery } from "gatsby"
import { graphql } from "gatsby"
import React, { createContext, useEffect, useReducer } from "react"
import {
  setDisclaimers,
  setOpenOnLoad,
  setDisclaimerQueue,
  advanceDisclaimerQueue,
} from "./actions"
import { Disclaimer, DisclaimersContextState } from "./disclaimers.d"
import disclaimersReducer from "./reducers"

// global flag to track if any disclaimer popover is open
export let isAnyDisclaimerPopoverOpen = false
export let isDisclaimerModalOpen = false
export let justClosedDisclaimerModal = false

// Global queue management
let globalDispatch: React.Dispatch<any> | null = null
let globalDisclaimerQueue: string[] = []
let globalCurrentIndex = 0
let wasModalOpenedFromQueue = false
export const setIsAnyDisclaimerPopoverOpen = (value: boolean) => {
  isAnyDisclaimerPopoverOpen = value
}

export const setIsDisclaimerModalOpen = (value: boolean) => {
  isDisclaimerModalOpen = value
  if (!value) {
    justClosedDisclaimerModal = true
    setTimeout(() => {
      justClosedDisclaimerModal = false
    }, 0) // reset after the current event loop

    // If modal was opened from queue and there are more disclaimers, advance the queue
    if (
      wasModalOpenedFromQueue &&
      globalDispatch &&
      globalDisclaimerQueue.length > 0 &&
      globalCurrentIndex < globalDisclaimerQueue.length - 1
    ) {
      globalDispatch(advanceDisclaimerQueue())
    }
    wasModalOpenedFromQueue = false
  }
}

// Functions to manage global queue state
export const setGlobalQueueState = (
  dispatch: React.Dispatch<any>,
  queue: string[],
  currentIndex: number,
) => {
  globalDispatch = dispatch
  globalDisclaimerQueue = queue
  globalCurrentIndex = currentIndex
}

export const setModalOpenedFromQueue = (value: boolean) => {
  wasModalOpenedFromQueue = value
}

const defaultState: DisclaimersContextState = {
  disclaimers: [],
  open: false,
  openOnLoad: [],
  activeDisclaimerPopoverId: null,
  isAnyDisclaimerPopoverOpen: false,
  disclaimerQueue: [],
  currentDisclaimerIndex: 0,
}

export const DisclaimersContext = createContext<
  [DisclaimersContextState, React.Dispatch<any>]
>([defaultState, () => {}])

export const DisclaimersProvider = ({
  children,
  value,
}: {
  children: React.ReactNode
  value: any
}) => {
  const allGlobalDisclaimers = useStaticQuery(
    GlobalDisclaimersQuery,
  )?.globalDisclaimers
  const globalDisclaimers =
    value?.language === "es"
      ? allGlobalDisclaimers?.globalDisclaimersES
      : allGlobalDisclaimers?.globalDisclaimers

  const [disclaimersState, dispatch] = useReducer(
    disclaimersReducer,
    defaultState,
  )

  // Keep global state in sync
  useEffect(() => {
    setGlobalQueueState(
      dispatch,
      disclaimersState.disclaimerQueue,
      disclaimersState.currentDisclaimerIndex,
    )
  }, [
    disclaimersState.disclaimerQueue,
    disclaimersState.currentDisclaimerIndex,
  ])

  useEffect(() => {
    const filteredGlobalDisclaimers = globalDisclaimers?.filter(
      (disclaimer: Disclaimer) => {
        const isDuplicate = value?.disclaimers?.some(
          (pageDisclaimer: Disclaimer) =>
            pageDisclaimer?.code === disclaimer?.code,
        )
        return isDuplicate ? null : disclaimer
      },
    )

    let pageAndGlobalDisclaimers = value?.disclaimers ? value.disclaimers : []

    if (filteredGlobalDisclaimers?.length > 0) {
      pageAndGlobalDisclaimers = [
        ...pageAndGlobalDisclaimers,
        ...filteredGlobalDisclaimers,
      ]
    }
    dispatch(setDisclaimers(pageAndGlobalDisclaimers))

    if (value?.openOnLoad) {
      dispatch(setOpenOnLoad(value.openOnLoad))
      // Set up the queue for sequential display
      dispatch(setDisclaimerQueue(value.openOnLoad))
    }
  }, [value])

  return (
    <DisclaimersContext.Provider value={[disclaimersState, dispatch]}>
      {children}
    </DisclaimersContext.Provider>
  )
}

const GlobalDisclaimersQuery = graphql`
  query GlobalDisclaimersQuery {
    globalDisclaimers: sanitySiteSettings {
      globalDisclaimers {
        code
        disclaimer
      }
      globalDisclaimersES {
        code
        disclaimer
      }
    }
  }
`
